var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DataSourceType, StepType } from "@types";
import { getAvailableSources, getSchema } from "@query-engine-service";
import { updateSource, updateStep } from "@components/StepConfigurator/StepConfigUtils";
import { hasValue } from "uxp/components";
export const InitialSourceId = '__initial_source__';
export const SourceOriginSQL = 'SQL Tables';
export const SourceOriginQuery = 'Saved Queries';
export function trimTrailingSlashes(str) {
    return str.replace(/\/+$/, '');
}
function getBaseURL(context) {
    var _a;
    const lucyUrl = trimTrailingSlashes(((_a = context === null || context === void 0 ? void 0 : context.uxpContext) === null || _a === void 0 ? void 0 : _a.lucyUrl) || '/');
    return `${lucyUrl}/components/QueryEngine`;
}
export function getEndPointUrl(context, endpoint) {
    const base = trimTrailingSlashes(getBaseURL(context));
    if (!endpoint.startsWith('/'))
        endpoint = '/' + endpoint;
    return `${base}${trimTrailingSlashes(endpoint)}`;
}
const RegisteredSteps = {};
export function handleStepFieldValueChanges(context, query, selectedStepId, field, value, source) {
    return __awaiter(this, void 0, void 0, function* () {
        let _query = (source ? updateSource : updateStep)(query, selectedStepId, field, value);
        if (source) {
            const sourceId = value.id;
            //since current step is not configured properly get query up to previous step to get the schema
            const tempQuery = getQueryUntilPreviousStep(_query, selectedStepId);
            const schema = yield (isDataSource(value) ? getSchemaForSource : getSchemaForStep)(context, tempQuery, sourceId);
            _query = appendSchema(_query, schema, sourceId, true);
        }
        return _query;
    });
}
export function handleMultipleStepFieldValueChanges(context, query, selectedStepId, updatedValues) {
    return __awaiter(this, void 0, void 0, function* () {
        for (const v of updatedValues) {
            query = yield handleStepFieldValueChanges(context, query, selectedStepId, v.field, v.value, v.source);
        }
        return query;
    });
}
export function registerStep(step) {
    const type = step.type;
    if (!RegisteredSteps[type]) {
        RegisteredSteps[type] = step;
    }
}
export function getRegisteredStepByType(type) {
    if (!type)
        return null;
    const sType = type;
    const step = RegisteredSteps[sType];
    return step;
}
export function getRegisteredSteps() {
    return Object.values(RegisteredSteps);
}
export function getDatasourceTypeFromId(id) {
    if (!id)
        return null;
    if (id.startsWith('sqltable'))
        return DataSourceType.SQL;
    if (id.startsWith('dx'))
        return DataSourceType.DX;
    else if (id.startsWith('collection'))
        return DataSourceType.COLLECTION;
    else if (id.startsWith('json'))
        return DataSourceType.JSON;
    else if (id.startsWith('lucy'))
        return DataSourceType.WORKFLOW;
    else if (id.startsWith('query'))
        return DataSourceType.Query;
    return null;
}
export function getSourceMetadataFromId(id) {
    const type = getDatasourceTypeFromId(id);
    if (!hasValue(type))
        return {
            type: null,
            metadata: null
        };
    if (type == DataSourceType.JSON)
        return {
            type,
            metadata: null,
            category: 'JSON'
        };
    // Everything after the FIRST colon — names may themselves contain colons
    // (e.g. auto-named saved queries like "All Locations_2026-07-16 19:12:47"),
    // which id.split(':')[1] would truncate.
    const metadataString = id.slice(id.indexOf(':') + 1);
    if (!hasValue(metadataString))
        return {
            type: null,
            metadata: null,
            category: ''
        };
    if (type == DataSourceType.SQL)
        return {
            type,
            metadata: {
                table: metadataString
            },
            category: 'Sql Table'
        };
    if (type == DataSourceType.DX)
        return {
            type,
            metadata: {
                table: metadataString
            },
            category: 'DX App'
        };
    const [model, collection] = metadataString.split('/');
    if (!hasValue(model) || !hasValue(collection))
        return {
            type: null,
            metadata: null,
            category: ''
        };
    if (type == DataSourceType.COLLECTION)
        return {
            type,
            metadata: {
                model,
                collection
            },
            category: model
        };
    if (type == DataSourceType.Query)
        return {
            type,
            metadata: {
                queryName: model,
                queryId: collection
            }
        };
    return {
        type,
        metadata: {
            model,
            action: collection
        },
        category: model
    };
}
export function hasHistoryEnabledForField(dataSource, field) {
    const historyFields = getHistoryEnabledFields(dataSource);
    return (historyFields === null || historyFields === void 0 ? void 0 : historyFields.map(f => f.name)).includes(field);
}
export function getHistoryEnabledFields(dataSource) {
    var _a;
    return (_a = ((dataSource === null || dataSource === void 0 ? void 0 : dataSource.fields) || [])) === null || _a === void 0 ? void 0 : _a.filter(f => { var _a; return (f === null || f === void 0 ? void 0 : f.hasOwnProperty('historicalData')) && (f === null || f === void 0 ? void 0 : f.historicalData) && ((_a = Object.keys(f.historicalData)) === null || _a === void 0 ? void 0 : _a.length) > 0; });
}
export function hasHistoryEnabledForSource(dataSource) {
    const historyFields = getHistoryEnabledFields(dataSource);
    return (historyFields === null || historyFields === void 0 ? void 0 : historyFields.length) > 0;
}
function isValidOrigin(id, origins) {
    var _a;
    if (!id || !origins || (origins === null || origins === void 0 ? void 0 : origins.length) == 0)
        return true;
    const origin = (_a = id.split(":")) === null || _a === void 0 ? void 0 : _a[0];
    if (!origin)
        return false;
    return origins.includes(origin);
}
export function prepareSources(catalogs, origins) {
    const sourceOrigins = {};
    const sources = catalogs.reduce((result, catalog) => {
        const { source, description, fields, params } = catalog;
        if (!isValidOrigin(source, origins))
            return result;
        const metadata = getSourceMetadataFromId(source);
        if (!metadata || !metadata.metadata)
            return result;
        const dataSource = {
            id: source,
            source: source,
            description: description,
            name: source,
            fields: fields,
            schema: fields,
            params: params || []
        };
        dataSource.type = metadata.type;
        const { model, action, collection, table, queryName, queryId } = metadata.metadata;
        if (metadata.type === DataSourceType.SQL) {
            dataSource.source = table;
            dataSource.name = table;
            dataSource.origin = SourceOriginSQL;
            if (!sourceOrigins[SourceOriginSQL])
                sourceOrigins[SourceOriginSQL] = {
                    sources: [],
                    types: [DataSourceType.SQL]
                };
            sourceOrigins[SourceOriginSQL].sources.push(dataSource.id);
        }
        else if (metadata.type === DataSourceType.DX) {
            dataSource.source = table;
            dataSource.name = table;
            dataSource.origin = table;
            if (!sourceOrigins[table])
                sourceOrigins[table] = {
                    sources: [],
                    types: [DataSourceType.DX]
                };
            sourceOrigins[table].sources.push(dataSource.id);
        }
        else if (metadata.type === DataSourceType.COLLECTION) {
            dataSource.model = model;
            dataSource.collection = collection;
            dataSource.name = collection;
            dataSource.origin = model;
            if (!sourceOrigins[model])
                sourceOrigins[model] = {
                    sources: [],
                    types: [DataSourceType.COLLECTION]
                };
            sourceOrigins[model].sources.push(dataSource.id);
            if (!sourceOrigins[model].types.includes(DataSourceType.COLLECTION))
                sourceOrigins[model].types.push(DataSourceType.COLLECTION);
        }
        else if (metadata.type === DataSourceType.WORKFLOW) {
            dataSource.model = model;
            dataSource.action = action;
            dataSource.name = action;
            dataSource.origin = model;
            if (!sourceOrigins[model])
                sourceOrigins[model] = {
                    sources: [],
                    types: [DataSourceType.WORKFLOW]
                };
            sourceOrigins[model].sources.push(dataSource.id);
            if (!sourceOrigins[model].types.includes(DataSourceType.WORKFLOW))
                sourceOrigins[model].types.push(DataSourceType.WORKFLOW);
        }
        else if (metadata.type == DataSourceType.Query) {
            dataSource.name = queryName;
            dataSource.queryName = queryName;
            dataSource.queryId = queryId;
            dataSource.origin = SourceOriginQuery;
            if (!sourceOrigins[SourceOriginQuery])
                sourceOrigins[SourceOriginQuery] = {
                    sources: [],
                    types: [DataSourceType.Query]
                };
            sourceOrigins[SourceOriginQuery].sources.push(dataSource.id);
        }
        dataSource.history = hasHistoryEnabledForSource(dataSource);
        result.push(dataSource);
        return result;
    }, []);
    return {
        sources: sources,
        origins: sourceOrigins
    };
}
/**
 * Function to perform a text search
 * @param value - The value to be searched
 * @param query - The query to search for
 * @param ignoreCase - Boolean to determine if the search should be case sensitive
 * @returns boolean - Returns true if the query is found in the value, false otherwise
 */
export function textSearch(value, query, ignoreCase = false) {
    if (ignoreCase) {
        return (value === null || value === void 0 ? void 0 : value.toLowerCase().indexOf(query === null || query === void 0 ? void 0 : query.toLowerCase())) !== -1;
    }
    else {
        return (value === null || value === void 0 ? void 0 : value.indexOf(query)) !== -1;
    }
}
export function isSavedQueryOnly(query) {
    var _a, _b, _c, _d, _e;
    return ((_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a.length) == 1 && ((_d = (_c = (_b = query === null || query === void 0 ? void 0 : query.sources) === null || _b === void 0 ? void 0 : _b[0]) === null || _c === void 0 ? void 0 : _c.id) === null || _d === void 0 ? void 0 : _d.startsWith('query')) // for saved queries 
        && ((_e = query === null || query === void 0 ? void 0 : query.steps) === null || _e === void 0 ? void 0 : _e.length) == 0 && (query === null || query === void 0 ? void 0 : query.sourceOnly);
}
/**
 * Function to generate the initial query
 * @param query - Optional query to initialize with
 * @returns IQuery - Returns the initial query
 */
export function generateInitialQuery(query) {
    const isSavedQuery = isSavedQueryOnly(query);
    return {
        sources: (query === null || query === void 0 ? void 0 : query.sources) || [],
        steps: (query === null || query === void 0 ? void 0 : query.steps) || [],
        sourceOnly: isSavedQuery,
        params: (query === null || query === void 0 ? void 0 : query.params) || []
    };
}
/**
 * generate a function to get the query buy selected step id
 * this will receive query and selectedStepId
 * if selected id is InitialSourceId get the first source from query , set steps to null
 * if a selected steps is null return the query
 * if selected step mathces any step id in steps, get the steps up to that step
 */
export function getQueryBySelectedStepId(query, selectedStepId) {
    const _query = generateInitialQuery(query);
    if (!hasValue(selectedStepId))
        return _query;
    if (selectedStepId === InitialSourceId) {
        return {
            sources: [_query.sources[0]],
            steps: []
        };
    }
    const stepIndex = _query.steps.findIndex(step => step.id === selectedStepId);
    if (stepIndex === -1) {
        return _query;
    }
    return {
        sources: _query.sources,
        steps: _query.steps.slice(0, stepIndex + 1)
    };
}
export function getQueryForSelectedSource(query, selectedSourceId) {
    var _a;
    const _query = generateInitialQuery(query);
    if (!hasValue(selectedSourceId))
        return _query;
    const source = (_a = _query === null || _query === void 0 ? void 0 : _query.sources) === null || _a === void 0 ? void 0 : _a.find(s => s.id === selectedSourceId);
    if (!source)
        return {
            sources: [],
            steps: []
        };
    return {
        sources: [source],
        steps: []
    };
}
/**
 * Function to get the query for the step before the selected step
 * @param query - The query object containing steps
 * @param selectedStepId - The ID of the selected step
 * @returns IQuery - Returns the query for the step before the selected step
 */
export function getQueryUntilPreviousStep(query, selectedStepId) {
    const _query = generateInitialQuery(query);
    if (!selectedStepId)
        return _query;
    if (selectedStepId === InitialSourceId) {
        return {
            sources: [_query.sources[0]],
            steps: []
        };
    }
    const stepIndex = _query.steps.findIndex(step => step.id === selectedStepId);
    if (stepIndex === -1)
        return _query;
    if (stepIndex == 0)
        return {
            sources: _query.sources,
            steps: []
        };
    return {
        sources: _query.sources,
        steps: _query.steps.slice(0, stepIndex)
    };
}
/**
 * Function to validate a query
 * This will go through each step and return a boolean indicating if the query is valid
 * @param query - The query object containing steps
 * @returns boolean - Returns true if the query is valid, otherwise false
 */
export function validateQuery(query) {
    var _a;
    if (!query)
        return {
            isValid: false,
            error: 'Invalid query'
        };
    if (!(query === null || query === void 0 ? void 0 : query.sources) || (query === null || query === void 0 ? void 0 : query.sources.length) === 0)
        return {
            isValid: false,
            error: 'Datasource has not selected'
        };
    if ((query === null || query === void 0 ? void 0 : query.steps) && ((_a = query === null || query === void 0 ? void 0 : query.steps) === null || _a === void 0 ? void 0 : _a.length) > 0) {
        for (const step of query.steps) {
            const { isValid } = validateStep(query, step);
            if (!isValid)
                return {
                    isValid: false,
                    error: `Step ${step.name} is not configured properly`
                };
        }
    }
    return {
        isValid: true,
        error: null
    };
}
/**
 * Function to get the selected step by its ID
 * @param query - The query object containing steps
 * @param stepId - The ID of the step to be retrieved
 * @returns { step: IStep | null, index: number, previousSteps: IStep[] } - Returns an object containing the step if found, its index, and the previous steps, otherwise null, -1, and an empty array
 */
export function getSelectedStepDetailsById(query, stepId) {
    if (!query || !query.steps || !stepId)
        return {
            step: null,
            index: -1,
            previousSteps: [],
            selectedSources: []
        };
    const index = query.steps.findIndex(step => step.id === stepId);
    const step = query.steps[index] || null;
    const previousSteps = step ? query.steps.slice(0, index) : [];
    const selectedSources = (query === null || query === void 0 ? void 0 : query.sources) || [];
    return {
        step,
        index,
        previousSteps,
        selectedSources
    };
}
/**
 * Function to find the source from query sources or steps by source ID
 * @param query - The query object containing sources and steps
 * @param sourceId - The ID of the source to be retrieved
 * @returns { IDataSource | null } - Returns the source object if found, otherwise null
 */
export function findSourceById(query, sourceId) {
    if (!hasValue(sourceId))
        return null;
    // Check in sources
    const source = query.sources.find(src => src.id === sourceId);
    if (source) {
        return source;
    }
    const step = query.steps.find(st => st.id === sourceId);
    if (step) {
        return step;
    }
    // If not found, return null
    return null;
}
/**
 * Function to find multiple sources from query sources or steps by an array of source IDs
 * @param query - The query object containing sources and steps
 * @param sourceIds - The array of source IDs to be retrieved
 * @returns { Array<IDataSource | IStep> } - Returns an array of source objects if found, otherwise an empty array
 */
export function findMultipleSourcesByIds(query, sourceIds) {
    if (!query || !sourceIds || sourceIds.length === 0)
        return [];
    const sources = [];
    for (const sourceId of sourceIds) {
        const source = findSourceById(query, sourceId);
        if (source)
            sources.push(source);
    }
    return sources;
}
/**
 * Function to find the next step ID based on the current step ID.
 * If there are steps after the current step, select the next one.
 * If not, try to select the step before the current step, or select the initial source.
 * @param query - The query object containing steps
 * @param currentStepId - The ID of the current step
 * @returns string - Returns the next step ID or the initial source ID
 */
export function findNextStepId(query, currentStepId) {
    if (!query || !query.steps || query.steps.length === 0)
        return InitialSourceId;
    const currentIndex = query.steps.findIndex(step => step.id === currentStepId);
    if (currentIndex === -1)
        return InitialSourceId;
    // If there are steps after the current step, select the next one
    if (currentIndex < query.steps.length - 1) {
        return query.steps[currentIndex + 1].id;
    }
    // If not, try to select the step before the current step
    if (currentIndex > 0) {
        return query.steps[currentIndex - 1].id;
    }
    // If no steps before or after, select the initial source
    return InitialSourceId;
}
export function findNextItemId(items, currentIndex) {
    var _a;
    if (items.length - 1 >= currentIndex + 1)
        return items[currentIndex + 1].id;
    return ((_a = items === null || items === void 0 ? void 0 : items[0]) === null || _a === void 0 ? void 0 : _a.id) || null;
}
/**
 * Function to check if a source is available in query sources
 * @param query - The query object containing sources
 * @param sourceId - The ID of the source to be checked
 * @returns boolean - Returns true if the source is found, otherwise false
 */
export function isSourceAvailableInQuery(query, sourceId) {
    return query.sources.some(src => src.id === sourceId);
}
export function isDataSource(source) {
    if (!source || typeof source !== 'object')
        return false;
    return 'type' in source && Object.values(DataSourceType).includes(source.type);
}
export function isStep(source) {
    return 'type' in source && Object.values(StepType).includes(source.type);
}
export function isBefore(step, sourceStep, query) {
    const sourceIndex = query.steps.findIndex(s => s.id === sourceStep.id);
    const stepIndex = query.steps.findIndex(s => s.id === step.id);
    if (sourceIndex == -1 || stepIndex == -1)
        return false;
    return sourceIndex < stepIndex;
}
/**
 * Function to validate step name and id
 * @param step - The step object to be validated
 * @returns Record<string, string> - Returns an object containing validation errors
 */
export function validateStepNameAndId(step) {
    const errors = {};
    if (!hasValue(step.id))
        errors.id = 'lqe.sc.id-required';
    if (!hasValue(step.name))
        errors.name = 'lqe.sc.name-required';
    return errors;
}
export function validateSource(query, step, sourceKey, history, returnSource) {
    if (!step || typeof step !== 'object') {
        return {
            isValid: false,
            error: 'lqe.sc.step-not-configured'
        };
    }
    const sourceId = step[sourceKey];
    if (!hasValue(sourceId)) {
        return {
            isValid: false,
            error: 'lqe.sc.source-required'
        };
    }
    const source = findSourceById(query, sourceId);
    if (!source) {
        return {
            isValid: false,
            error: 'lqe.sc.invalid-source'
        };
    }
    if (history && (!isDataSource(source) || !hasHistoryEnabledForSource(source)))
        return {
            isValid: false,
            error: 'lqe.sc.no-history-source'
        };
    if (isStep(source) && !isBefore(step, source, query))
        return {
            isValid: false,
            error: 'lqe.sc.source-after-current'
        };
    return {
        isValid: true,
        source
    };
}
/**
 * Function to validate a source in query by sourceId
 * @param query - The query object containing sources
 * @param sourceId - The ID of the source to be validated
 * @returns { isValid: boolean, error?: string } - Returns an object containing validation status and error message if any
 */
export function validateSourceById(query, sourceId) {
    if (!sourceId) {
        return {
            isValid: false,
            error: 'lqe.sc.source-required'
        };
    }
    if (!findSourceById(query, sourceId)) {
        return {
            isValid: false,
            error: 'lqe.sc.invalid-source'
        };
    }
    return {
        isValid: true
    };
}
/**
 * Function to validate a step
 * @param step - The step object to be validated
 * @returns { isValid: boolean, errors: Record<string, string> } - Returns an object containing validation status and errors
 */
export function validateStep(query, step) {
    if (!step || typeof step !== 'object')
        return {
            isValid: false,
            errors: {
                step: "step is required"
            }
        };
    const registeredStep = getRegisteredStepByType(step.type);
    if (!registeredStep)
        return {
            isValid: false,
            errors: {
                type: "invalid step type"
            }
        };
    return registeredStep.validate(query, step);
}
function requestSchema(context, query) {
    return __awaiter(this, void 0, void 0, function* () {
        try {
            const res = yield getSchema(context, query);
            if (!res || !Array.isArray(res)) {
                console.log('Invalid response received for schema request');
                return [];
            }
            return res;
        }
        catch (e) {
            console.log('Error fetching schema:', e);
            return [];
        }
    });
}
function findSchemaById(schemas, id) {
    const ss = (schemas || []).find((s) => s.id === id);
    if (ss && (ss === null || ss === void 0 ? void 0 : ss.schema) && ss.schema.length > 0)
        return ss.schema;
    console.log(`Unable to find a valid schema for the id: ${id}. schemas: ${schemas}`);
    return [];
}
export function getSchemaForQuery(context, query) {
    return __awaiter(this, void 0, void 0, function* () {
        const { isValid } = validateQuery(query);
        if (!isValid) {
            console.log('Unable to get the schema for query. Query is not configured properly');
            return [];
        }
        return requestSchema(context, query);
    });
}
export function getOutputSchemaForQuery(context, query) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        const { isValid } = validateQuery(query);
        if (!isValid)
            return null;
        const schemas = yield requestSchema(context, query);
        if (schemas.length == 0)
            return null;
        // lst step 
        const lastStep = ((_a = query === null || query === void 0 ? void 0 : query.steps) === null || _a === void 0 ? void 0 : _a.length) > 0 ? query.steps[query.steps.length - 1] : query.sources[0];
        if (!lastStep)
            return null;
        const schema = schemas.find(sc => sc.id === lastStep.id);
        return (schema === null || schema === void 0 ? void 0 : schema.schema) || null;
    });
}
export function getSchemaForSource(context, query, sourceId) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        // here we dont need to get schema other sources or steps.
        // so just pass the source (data source )
        const source = (_a = query === null || query === void 0 ? void 0 : query.sources) === null || _a === void 0 ? void 0 : _a.find(s => s.id == sourceId);
        if (!source)
            return [];
        const schemas = yield requestSchema(context, {
            sources: [source],
            steps: []
        });
        return findSchemaById(schemas, sourceId);
    });
}
export function getSchemaForStep(context, query, stepId) {
    return __awaiter(this, void 0, void 0, function* () {
        const _query = getQueryBySelectedStepId(query, stepId);
        const { step: _step } = getSelectedStepDetailsById(_query, stepId);
        const { isValid } = validateStep(_query, _step);
        if (!isValid) {
            console.log('Unable to get the schema, step is not configured properly');
            return [];
        }
        const schemas = yield requestSchema(context, query);
        return findSchemaById(schemas, stepId);
    });
}
export function appendSchema(query, schema, id, source) {
    if (source) {
        const sourceIndex = query.sources.findIndex(s => s.id === id);
        if (sourceIndex !== -1) {
            query.sources[sourceIndex].schema = schema;
        }
        else {
            console.log(`Source with id ${id} not found in query sources.`);
        }
    }
    else {
        const stepIndex = query.steps.findIndex(s => s.id === id);
        if (stepIndex !== -1) {
            query.steps[stepIndex].schema = schema;
        }
        else {
            console.log(`Step with id ${id} not found in query steps.`);
        }
    }
    return query;
}
/**
 * Function to update schema for each source and step in a query
 * @param context - The query builder context
 * @param query - The query object containing sources and steps
 * @returns Promise<IQuery> - Returns a promise that resolves to the updated query with schemas
 */
export function updateSchemasInQuery(context, query) {
    return __awaiter(this, void 0, void 0, function* () {
        const { isValid } = validateQuery(query);
        if (!isValid) {
            console.log('Unable to update schemas, query is not configured properly');
            return query;
        }
        const schemas = yield requestSchema(context, query);
        // Update schemas for sources
        query.sources = query.sources.map(source => {
            const schema = findSchemaById(schemas, source.id);
            if (schema) {
                source.schema = schema;
            }
            return source;
        });
        // Update schemas for steps
        query.steps = query.steps.map(step => {
            const schema = findSchemaById(schemas, step.id);
            if (schema) {
                step.schema = schema;
            }
            return step;
        });
        return query;
    });
}
export function getUniqueFieldsInSource(query, sourceId) {
    var _a;
    const source = findSourceById(query, sourceId);
    const fields = new Set((_a = source === null || source === void 0 ? void 0 : source.schema) === null || _a === void 0 ? void 0 : _a.map(f => f.name));
    return Array.from(fields);
}
export function validateFieldConfiguration(query, step, selectedFields, sourceField) {
    const sourceId = step[sourceField];
    if (!hasValue(sourceId))
        return {
            isValid: false,
            error: 'Invalid source field'
        };
    const source = findSourceById(query, sourceId);
    if (!source)
        return {
            isValid: false,
            error: 'Invalid source selected'
        };
    const sourceSchema = source.schema || [];
    const schemaFields = sourceSchema.map(field => field === null || field === void 0 ? void 0 : field.name);
    const sourceContainsAllFields = selectedFields.every(field => schemaFields.includes(field));
    return {
        isValid: sourceContainsAllFields,
        error: sourceContainsAllFields ? null : 'Some of the fields are not available in the source'
    };
}
export function copyToClipboard(text) {
    navigator.clipboard.writeText(text);
}
export function prepareQuery(context, p, origins) {
    return __awaiter(this, void 0, void 0, function* () {
        var _a;
        if (p.steps && p.sources) {
            let { sources: allSources } = prepareSources(((_a = (yield getAvailableSources(context))) === null || _a === void 0 ? void 0 : _a.definitions) || [], origins || []);
            for (let i = 0; i < p.sources.length; i++) {
                let s = p.sources[i];
                if (typeof s == 'string') {
                    let source = allSources.find(x => x.id == s);
                    const schema = yield getSchemaForSource(context, {
                        sources: [source],
                        steps: []
                    }, source.id);
                    source.schema = schema;
                    p.sources[i] = source;
                }
            }
            for (let s of p.steps) {
                s.name = s.name || s.id;
                const schema = yield getSchemaForStep(context, getQueryBySelectedStepId(p, s.id), s.id);
                s.schema = schema;
            }
        }
        return p;
    });
}
export function distinct(array, field) {
    const distinctValues = [...new Set(array.map(item => item[field]))];
    return distinctValues;
}
export function validateParam(query, param) {
    var _a;
    const _query = generateInitialQuery(query);
    const errors = {};
    // Validate name presence
    if (!hasValue(param.name)) {
        errors.name = "Parameter name is required.";
    }
    // Validate uniqueness
    const existingNames = ((_a = _query.params) === null || _a === void 0 ? void 0 : _a.map(p => p.name)) || [];
    const duplicateCount = existingNames.filter(name => name === param.name).length;
    if (duplicateCount > 1) {
        errors.name = `Duplicate parameter name "${param.name}".`;
    }
    if (!hasValue(param.type)) {
        errors.type = "Parameter type is required";
    }
    return {
        isValid: Object.keys(errors).length === 0,
        errors
    };
}
export function validateParams(query) {
    return ((query === null || query === void 0 ? void 0 : query.params) || []).every(p => {
        const { isValid } = validateParam(query, p);
        return isValid;
    });
}
